import React, { useEffect, useRef, useState } from 'react';
import BaileyAvatar, { BaileyStill } from './BaileyAvatar';
import { OFFERED_HAIRSTYLES, HAIR_GROUPS, HAIR_TECHNIQUES, TATTOO_INKS, appearanceKey, normaliseAppearance } from './appearance';
import { HAIR_SHADES, TEE_COLOURS, NAIL_COLOURS, LINER_COLOURS, LIP_COLOURS, BLUSH_COLOURS, shadeName, surpriseLook } from './styleCatalogue';
import { TATTOOS, tattooLayers, bandSvgParts } from './tattoos';
import { createDirector } from './reactions';
import { hapticSuccess, hapticTick } from '../../../_utils/haptics';

// A haircut tile: Bailey photographed in the cut (cutThumbs.js), with the
// flat drawing until the picture's taken, or where it can't be. Waits a
// moment after each change, so dragging through colours isn't a picture a step.
let thumbs = null;
function CutPicture({ appearance }) {
    const key = appearanceKey(appearance);
    const [src, setSrc] = useState(() => (thumbs ? thumbs.takenPicture(appearance) : null));
    useEffect(() => {
        let alive = true;
        const t = setTimeout(() => {
            import(/* webpackChunkName: "bailey-avatar" */ './cutThumbs')
                .then((m) => { thumbs = m; return m.pictureOf(appearance); })
                .then((url) => { if (alive && url) setSrc(url); })
                .catch(() => {});
        }, thumbs && thumbs.takenPicture(appearance) ? 0 : 150);
        return () => { alive = false; clearTimeout(t); };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return src ? <img src={src} alt="" draggable="false" /> : <BaileyStill appearance={appearance} face />;
}

// Tabs run down the body, and the camera follows the tab.
const SECTIONS = [
    { id: 'hair', name: 'Hair' },
    { id: 'makeup', name: 'Makeup' },
    { id: 'tee', name: 'Top' },
    { id: 'tattoos', name: 'Tattoos', extra: true },
    { id: 'nails', name: 'Nails' }
];
const MIN_COVER = 900; // ms of "no peeking" even when the save is instant
const REVEAL = { confetti: 1300, line: 3300, done: 4300, calm: 1700 };

const reducedMotion = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};
const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;

/* ------------------------------------------------------------ colour mixer */
function hexToHsl(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return { h: Math.round(h * 60), s: Math.round(s * 100), l: Math.round(l * 100) };
}
function hslToHex({ h, s, l }) {
    const S = s / 100, L = l / 100;
    const k = n => (n + h / 30) % 12;
    const a = S * Math.min(L, 1 - L);
    const f = n => L - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return '#' + [f(0), f(8), f(4)].map(x => Math.round(x * 255).toString(16).padStart(2, '0')).join('');
}
/** Mix your own: hue, intensity and shade sliders, plus a hex field. */
function Mixer({ label, value, onChange }) {
    const [hsl, setHsl] = useState(() => hexToHsl(value));
    const [text, setText] = useState(value);
    // A swatch picked while mixing moves the sliders to match.
    useEffect(() => {
        setText(value);
        setHsl(current => (hslToHex(current) === value ? current : hexToHsl(value)));
    }, [value]);
    const set = patch => {
        const next = { ...hsl, ...patch };
        setHsl(next);
        onChange(hslToHex(next));
    };
    const hue = `hsl(${hsl.h}, 100%, 50%)`;
    return (
        <div className="bly-mixer" role="group" aria-label={`Mix your own ${label.toLowerCase()}`}>
            <label><span>Hue</span>
                <input type="range" min="0" max="359" value={hsl.h} style={{ '--track': 'linear-gradient(90deg,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)' }}
                    onChange={e => { const h = Number(e.target.value); set({ h }); }} /></label>
            <label><span>Intensity</span>
                <input type="range" min="0" max="100" value={hsl.s} style={{ '--track': `linear-gradient(90deg, hsl(${hsl.h},0%,${hsl.l}%), hsl(${hsl.h},100%,${hsl.l}%))` }}
                    onChange={e => { const s = Number(e.target.value); set({ s }); }} /></label>
            <label><span>Shade</span>
                <input type="range" min="4" max="96" value={hsl.l} style={{ '--track': `linear-gradient(90deg, #000, ${hue}, #fff)` }}
                    onChange={e => { const l = Number(e.target.value); set({ l }); }} /></label>
            <label className="bly-mixer-hex"><span>Hex</span>
                <input type="text" value={text} maxLength={7} spellCheck="false" autoComplete="off"
                    onChange={e => {
                        const raw = e.target.value.trim();
                        const next = raw.charAt(0) === '#' ? raw : `#${raw}`;
                        setText(next);
                        if (/^#[0-9a-f]{6}$/i.test(next)) { setHsl(hexToHsl(next.toLowerCase())); onChange(next.toLowerCase()); }
                    }} /></label>
        </div>
    );
}

/* ------------------------------------------------------------ swatch rails */
/**
 * One part's colour bar. Swatches are shaped like the real thing (a hair
 * tress, a nail, a tee, a lipstick mark). `none` adds a first "None"/"Bare"
 * swatch. The last swatch mixes your own colour.
 */
function Rail({ label, shades, value, shape, onPick, none, mixing, onMix }) {
    const off = none && none.selected;
    const custom = !off && !shades.some(s => s.hex === value);
    const groups = [];
    shades.forEach(s => {
        const name = s.group || '';
        const group = groups.find(g => g.name === name);
        if (group) group.shades.push(s); else groups.push({ name, shades: [s] });
    });
    const current = off ? none.label : shadeName(shades, value);
    return (
        <div className="bly-rail">
            <div className="bly-rail-head"><strong>{label}</strong><span aria-live="polite">{current}</span></div>
            {groups.map((group, gi) => (
                <div key={group.name || gi} className="bly-rail-group">
                    {group.name && groups.length > 1 && <span className="bly-rail-group-name">{group.name}</span>}
                    <div className={`bly-swatches is-${shape}`} role="group" aria-label={group.name ? `${label}: ${group.name}` : label}>
                        {gi === 0 && none && (
                            <button type="button" className="bly-swatch is-none" aria-pressed={!!off} aria-label={none.label} title={none.label} onClick={none.onPick}><i /></button>
                        )}
                        {group.shades.map(s => (
                            <button key={s.hex} type="button" className="bly-swatch" style={{ '--c': s.hex }} aria-pressed={!off && s.hex === value}
                                aria-label={s.name} title={s.name} onClick={() => onPick(s.hex)}><i /></button>
                        ))}
                        {gi === groups.length - 1 && (
                            <button type="button" className={`bly-swatch is-mix${custom ? ' is-custom' : ''}`} style={custom ? { '--c': value } : undefined}
                                aria-pressed={custom || mixing} aria-expanded={!!mixing} aria-label="Mix your own" title="Mix your own" onClick={onMix}><i /></button>
                        )}
                    </div>
                </div>
            ))}
            {mixing && <Mixer label={label} value={off ? shades[0].hex : value} onChange={onPick} />}
        </div>
    );
}

function Chips({ label, options, value, onPick, className = '' }) {
    return (
        <div className={`bly-chips ${className}`} role="group" aria-label={label}>
            {options.map(o => <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onPick(o.id)}>{o.name}</button>)}
        </div>
    );
}

function TattooThumb({ id, ink }) {
    const design = TATTOOS.find(t => t.id === id);
    const parts = design.band ? bandSvgParts(id, ink) : tattooLayers(design, ink);
    return (
        <svg viewBox="-6 -6 112 112" aria-hidden="true" focusable="false">
            {parts.map((p, i) => p.kind === 'text'
                ? <text key={i} x={p.x} y={p.y} textAnchor="middle" fontSize={p.size} fontWeight="700" fontFamily="Georgia, serif" fill="#1b2328">{p.text}</text>
                : <path key={i} d={p.d} fill={p.kind === 'fill' ? p.colour : 'none'} stroke={p.kind === 'line' ? p.colour : 'none'} strokeWidth={p.width} strokeLinejoin="round" strokeLinecap="round" />)}
        </svg>
    );
}

/* ------------------------------------------------------------- the chair */
export default function BaileyWardrobe({ appearance, savedAppearance, saving, error, extras = false, onChange, onSave, onDone, onClose }) {
    const avatar = useRef(null);
    const director = useRef(null);
    if (!director.current) director.current = createDirector();
    const timers = useRef({});
    const alive = useRef(true);
    const [section, setSection] = useState('hair');
    const [armSide, setArmSide] = useState('left');
    const [mixing, setMixing] = useState(null);
    const [paused, setPaused] = useState(false);
    const [bubble, setBubble] = useState(null);
    const [phase, setPhase] = useState('styling'); // styling | cover | reveal
    const [confetti, setConfetti] = useState(null);
    const phaseRef = useRef(phase);
    phaseRef.current = phase;
    const lastArm = useRef('left');
    const value = normaliseAppearance(appearance);
    const latest = useRef(value);
    latest.current = value;
    const sections = SECTIONS.filter(s => extras || !s.extra);
    const current = sections.find(s => s.id === section) || sections[0];
    const busy = saving || phase !== 'styling';
    const changed = appearanceKey(value) !== appearanceKey(savedAppearance);

    const clear = name => { if (timers.current[name]) { clearTimeout(timers.current[name]); delete timers.current[name]; } };
    const after = (name, ms, fn) => { clear(name); timers.current[name] = setTimeout(() => { delete timers.current[name]; if (alive.current) fn(); }, ms); };
    useEffect(() => () => { alive.current = false; Object.keys(timers.current).forEach(clear); }, []);

    function say(text) {
        if (!text) return;
        setBubble({ text, key: Date.now() });
        after('bubble', 2300, () => setBubble(null));
    }
    // Ask the director whether Bailey reacts, once its next check is due.
    function schedule() {
        const at = director.current.nextCheck();
        if (at === null) return;
        after('react', Math.max(0, (at - clock()) * 1000) + 30, () => {
            if (phaseRef.current !== 'styling') return;
            const reaction = director.current.decide(clock());
            if (reaction && avatar.current) {
                const a = latest.current;
                let { id, line } = reaction;
                // Show off the arm that was last inked; a wink if it was cleared.
                const side = id === 'showArm' && lastArm.current === 'right' ? -1 : 1;
                if (id === 'showArm' && a[side === 1 ? 'tattooLeft' : 'tattooRight'] === 'none') { id = 'wink'; line = null; }
                avatar.current.react(id, side);
                say(line);
            }
            schedule();
        });
    }

    function update(patch, field) {
        if (busy) return;
        const before = latest.current;
        const next = normaliseAppearance({ ...before, ...patch });
        const changes = field === 'surprise' ? [{ field: 'surprise' }]
            : Object.keys(patch).filter(k => before[k] !== next[k]).map(k => ({ field: k, from: before[k], to: next[k] }));
        if (!changes.length) return;
        if (avatar.current) avatar.current.react(null);
        director.current.change(changes, clock());
        schedule();
        onChange(next);
    }

    // Makeup parts: picking a colour switches that part on. Coming from no
    // makeup at all, only that part goes on.
    function makeupPick(part, hex) {
        const on = { liner: 'linerOn', lips: 'lipsOn', blush: 'blushOn' }[part];
        const colour = { liner: 'linerColour', lips: 'lipColour', blush: 'blushColour' }[part];
        if (!value.makeupEnabled) update({ makeupEnabled: true, linerOn: part === 'liner', lipsOn: part === 'lips', blushOn: part === 'blush', [colour]: hex });
        else update({ [on]: true, [colour]: hex });
    }
    function makeupNone(part) {
        const on = { liner: 'linerOn', lips: 'lipsOn', blush: 'blushOn' }[part];
        if (!value.makeupEnabled) return;
        update({ [on]: false });
    }
    const partOn = part => value.makeupEnabled && value[{ liner: 'linerOn', lips: 'lipsOn', blush: 'blushOn' }[part]];

    function pickSection(id) {
        if (busy || id === section) return;
        setSection(id); setMixing(null); hapticTick();
    }

    async function save() {
        if (busy || !changed) return;
        director.current.quiet(); clear('react'); setBubble(null); setMixing(null);
        setPhase('cover');
        if (avatar.current) avatar.current.reveal('cover');
        const started = Date.now();
        let ok = false;
        try { ok = (await onSave()) !== false; } catch (e) { ok = false; }
        const wait = Math.max(0, MIN_COVER - (Date.now() - started));
        if (wait) await new Promise(resolve => setTimeout(resolve, wait));
        if (!alive.current) return;
        if (!ok) {
            if (avatar.current) avatar.current.reveal('fail');
            after('fail', 1600, () => avatar.current && avatar.current.reveal(null));
            setPhase('styling');
            return;
        }
        setPhase('reveal');
        if (avatar.current) avatar.current.reveal('go');
        if (reducedMotion() || paused) {
            hapticSuccess(); say('Looking sharp.');
            after('done', REVEAL.calm, finish);
            return;
        }
        after('confetti', REVEAL.confetti, () => { hapticSuccess(); setConfetti(Date.now()); });
        after('line', REVEAL.line, () => say('Looking sharp.'));
        after('done', REVEAL.done, finish);
    }
    function finish() {
        ['confetti', 'line', 'done', 'bubble'].forEach(clear);
        if (avatar.current) avatar.current.reveal(null);
        if (onDone) onDone(); else onClose();
    }
    // Skip the reveal: a tap anywhere, or Escape.
    useEffect(() => {
        if (phase !== 'reveal') return undefined;
        const key = e => { if (e.key === 'Escape') finish(); };
        window.addEventListener('keydown', key);
        return () => window.removeEventListener('keydown', key);
    });

    const shot = phase !== 'styling' ? 'full'
        : current.id === 'makeup' ? 'face'
            : current.id === 'nails' ? 'nails'
                : current.id === 'tattoos' ? (armSide === 'left' ? 'arm-left' : 'arm-right') : 'full';
    const technique = HAIR_TECHNIQUES.find(t => t.id === value.hairTechnique);
    const secondLabel = extras ? technique.second : 'Highlights';
    const armKey = armSide === 'left' ? 'tattooLeft' : 'tattooRight';
    const mix = field => ({ mixing: mixing === field, onMix: () => setMixing(m => (m === field ? null : field)) });
    const confettiColours = [value.hairColour, value.hairAccent, value.shirtColour, value.nailsEnabled ? value.nailColour : '#e2b53a', value.makeupEnabled ? value.lipColour : '#f2c1cc', '#007ea0', '#e2b53a'];

    let message = error || 'Try anything. Your changes are saved when you’re happy.';
    if (!error && phase === 'cover') message = 'No peeking…';
    if (!error && phase === 'reveal') message = 'Saved. Tap anywhere to skip.';

    return (
        <section className={`bailey-studio is-${phase}`} id="bailey-wardrobe" aria-labelledby="bailey-studio-title"
            onClick={phase === 'reveal' ? finish : undefined}>
            <header className="bailey-studio-heading">
                <div><span className="bailey-eyebrow">BEU / THE STYLE CHAIR</span><h2 id="bailey-studio-title">Make Bailey yours.</h2></div>
                <button type="button" className="bailey-text-button" disabled={busy} onClick={onClose}>Cancel</button>
            </header>
            <div className="bailey-studio-body">
                <div className="bailey-studio-stage">
                    <div className="bailey-studio-stage-copy" aria-hidden="true"><span>TAKE A SEAT.</span><p>One Bailey. Your style.</p></div>
                    <div className="bailey-studio-model">
                        <BaileyAvatar ref={avatar} appearance={value} studio paused={paused} shot={shot} />
                        {bubble && <div key={bubble.key} className="bly-speech" aria-hidden="true">{bubble.text}</div>}
                        {confetti && (
                            <div key={confetti} className="bly-confetti" aria-hidden="true">
                                {Array.from({ length: 36 }, (_, i) => {
                                    const angle = (i / 36) * Math.PI * 2 + Math.sin(i * 7.3) * 0.3;
                                    const reach = 120 + ((i * 53) % 90);
                                    return <i key={i} style={{
                                        '--c': confettiColours[i % confettiColours.length],
                                        '--dx': `${Math.round(Math.cos(angle) * reach)}px`, '--dy': `${Math.round(Math.sin(angle) * reach * 0.7 - 60)}px`,
                                        '--r': `${(i * 97) % 360}deg`, '--d': `${(i % 6) * 25}ms`, '--w': `${6 + (i % 3) * 2}px`
                                    }} />;
                                })}
                            </div>
                        )}
                        {phase === 'reveal' && <p className="bailey-sr-only" role="status">Saved. Bailey loves the new look.</p>}
                    </div>
                    <div className="bailey-turn-controls" aria-label="Preview angle">
                        <button type="button" aria-label="Turn Bailey left" disabled={busy} onClick={() => avatar.current && avatar.current.rotateBy(-Math.PI / 4)}>↶</button>
                        <button type="button" disabled={busy} onClick={() => avatar.current && avatar.current.spin()}>Give it a spin</button>
                        <button type="button" aria-label="Turn Bailey right" disabled={busy} onClick={() => avatar.current && avatar.current.rotateBy(Math.PI / 4)}>↷</button>
                    </div>
                    <div className="bailey-stage-footer"><span>Drag to turn the chair</span><button type="button" className="bailey-text-button" disabled={busy} onClick={() => avatar.current && avatar.current.resetRotation()}>Front view</button><button type="button" className="bailey-text-button" aria-pressed={!paused} onClick={() => setPaused(p => !p)}>Motion {paused ? 'off' : 'on'}</button></div>
                </div>
                <div className="bailey-studio-options">
                    <div className="bailey-category-tabs" role="group" aria-label="Style category">
                        {sections.map(item => <button key={item.id} type="button" aria-pressed={current.id === item.id} disabled={busy && current.id !== item.id} onClick={() => pickSection(item.id)}>{item.name}</button>)}
                    </div>
                    <fieldset disabled={busy} className="bailey-style-controls">
                        <legend className="bailey-sr-only">{current.name} settings</legend>

                        {current.id === 'hair' && <>
                            {HAIR_GROUPS.map(group => (
                                <div key={group} className="bly-cut-group">
                                    <span className="bly-rail-group-name">{group}</span>
                                    <div className="bly-cuts" role="group" aria-label={`Hairstyle: ${group.toLowerCase()}`}>
                                        {/* The original (no hair) leads the short cuts; the newer cuts need the chair's newer fields */}
                                        {OFFERED_HAIRSTYLES.filter(hair => (hair.group || 'Short') === group && (extras || !hair.first)).map(hair => (
                                            <button key={hair.id} type="button" className="bly-cut" aria-pressed={value.hairStyle === hair.id} onClick={() => update({ hairStyle: hair.id })}>
                                                <span className="bly-cut-art"><CutPicture appearance={{ ...value, hairStyle: hair.id }} /></span>
                                                <strong>{hair.name}</strong>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {value.hairStyle === 'none' ? <p className="bailey-style-hint">Choose a cut to play with colour.</p> : <>
                                {extras && <Chips label="Colour technique" className="is-technique" options={HAIR_TECHNIQUES} value={value.hairTechnique} onPick={id => update({ hairTechnique: id })} />}
                                <Rail label="Colour" shape="tress" shades={HAIR_SHADES} value={value.hairColour} onPick={hex => update({ hairColour: hex })} {...mix('hairColour')} />
                                {secondLabel && <Rail label={secondLabel} shape="tress" shades={HAIR_SHADES} value={value.hairAccent} onPick={hex => update({ hairAccent: hex })} {...mix('hairAccent')} />}
                            </>}
                        </>}

                        {current.id === 'makeup' && <>
                            {!extras && <Chips label="Makeup" options={[{ id: 'off', name: 'None' }, { id: 'on', name: 'Made up' }]} value={value.makeupEnabled ? 'on' : 'off'} onPick={id => update({ makeupEnabled: id === 'on' })} />}
                            {[['liner', 'Liner', LINER_COLOURS, 'linerColour', 'liner'], ['lips', 'Lips', LIP_COLOURS, 'lipColour', 'lips'], ['blush', 'Blush', BLUSH_COLOURS, 'blushColour', 'blush']].map(([part, label, shades, field, shape]) => (
                                (extras || value.makeupEnabled) && <Rail key={part} label={label} shape={shape} shades={shades} value={value[field]}
                                    none={extras ? { label: 'None', selected: !partOn(part), onPick: () => makeupNone(part) } : undefined}
                                    onPick={hex => (extras ? makeupPick(part, hex) : update({ [field]: hex }))} {...mix(field)} />
                            ))}
                        </>}

                        {current.id === 'tee' && <>
                            <div className="bailey-section-intro"><h3>The Beu top.</h3><p>Same crew. A colour of your own.</p></div>
                            <Rail label="Top colour" shape="tee" shades={TEE_COLOURS} value={value.shirtColour} onPick={hex => update({ shirtColour: hex })} {...mix('shirtColour')} />
                        </>}

                        {current.id === 'tattoos' && <>
                            <Chips label="Which arm" className="is-arms" options={[{ id: 'left', name: 'Left arm' }, { id: 'right', name: 'Right arm' }]} value={armSide} onPick={id => { setArmSide(id); lastArm.current = id; }} />
                            <Chips label="Ink" className="is-ink" options={TATTOO_INKS} value={value.tattooInk} onPick={id => update({ tattooInk: id })} />
                            <div className="bly-rail-head"><strong>Flash sheet</strong><span aria-live="polite">{value[armKey] === 'none' ? 'Nothing yet' : TATTOOS.find(t => t.id === value[armKey]).name}</span></div>
                            <div className="bly-flash" role="group" aria-label={`Tattoo on the ${armSide} arm`}>
                                <button type="button" className="bly-flash-item is-none" aria-pressed={value[armKey] === 'none'} onClick={() => { lastArm.current = armSide; update({ [armKey]: 'none' }); }}><span /><strong>None</strong></button>
                                {TATTOOS.map(t => (
                                    <button key={t.id} type="button" className="bly-flash-item" aria-pressed={value[armKey] === t.id} onClick={() => { lastArm.current = armSide; update({ [armKey]: t.id }); }}>
                                        <TattooThumb id={t.id} ink={value.tattooInk} /><strong>{t.name}</strong>
                                    </button>
                                ))}
                            </div>
                            <p className="bly-note">Left and right are Bailey’s own. The chair turns so you can see the work.</p>
                        </>}

                        {current.id === 'nails' && <>
                            <Rail label="Polish" shape="nail" shades={NAIL_COLOURS} value={value.nailColour}
                                none={{ label: 'Bare', selected: !value.nailsEnabled, onPick: () => update({ nailsEnabled: false }) }}
                                onPick={hex => update({ nailsEnabled: true, nailColour: hex })} {...mix('nailColour')} />
                        </>}
                    </fieldset>
                </div>
            </div>
            <div className="bailey-studio-save" role="group" aria-label="Save">
                <p role={error ? 'alert' : undefined}>{message}</p>
                <div className="bailey-studio-actions">
                    <button type="button" className="agt-btn" disabled={busy} onClick={() => update(surpriseLook(latest.current, extras), 'surprise')}>Surprise me</button>
                    <button type="button" className="agt-btn is-primary" onClick={save} disabled={busy || !changed}>{phase === 'cover' || saving ? 'Saving…' : phase === 'reveal' ? 'Saved' : 'Save my Bailey'}</button>
                </div>
            </div>
        </section>
    );
}
